import React, { useMemo } from 'react';
import './EventCalendarInspector.css';

function EventCalendarInspector({ func, index, onUpdate }) {
  // Get all available locales
  const availableLocales = useMemo(() => {
    // Define a list of common locales
    const commonLocales = [
      'en-US', // English (United States)
      'en-GB', // English (United Kingdom)
      'cs-CZ', // Czech
      'de-DE', // German
      'es-ES', // Spanish
      'fr-FR', // French
      'it-IT', // Italian
      'ja-JP', // Japanese
      'ko-KR', // Korean
      'pl-PL', // Polish
      'pt-BR', // Portuguese (Brazil)
      'pt-PT', // Portuguese (Portugal)
      'ru-RU', // Russian
      'zh-CN', // Chinese (Simplified)
      'nl-NL', // Dutch
      'sv-SE', // Swedish
      'da-DK', // Danish
      'fi-FI', // Finnish
      'nb-NO', // Norwegian
      'tr-TR', // Turkish
      'ar-SA', // Arabic
      'hi-IN', // Hindi
      'th-TH', // Thai
      'vi-VN', // Vietnamese
      'sk-SK', // Slovak
      'uk-UA', // Ukrainian
      'hu-HU', // Hungarian
      'ro-RO', // Romanian
      'bg-BG', // Bulgarian
      'hr-HR', // Croatian
      'el-GR', // Greek
      'he-IL'  // Hebrew
    ];

    try {
      // Filter to only include locales that are actually supported by the browser
      return Intl.DateTimeFormat.supportedLocalesOf(commonLocales);
    } catch (error) {
      console.error('Error getting locales:', error);
      return ['en-US', 'cs-CZ']; // Fallback locales
    }
  }, []);

  // Format locale for display
  const formatLocaleDisplay = (locale) => {
    try {
      const [language, region] = locale.split('-');
      
      let displayText = locale;
      
      try {
        // Use the locale's own name in English for consistency
        const languageName = new Intl.DisplayNames(['en'], { type: 'language' })
          .of(language);
        
        const regionName = region ? 
          new Intl.DisplayNames(['en'], { type: 'region' })
            .of(region) : '';
            
        displayText = regionName ? 
          `${languageName} - ${regionName}` : 
          languageName;
      } catch (e) {
        console.warn(`Could not format locale ${locale}:`, e);
      }
      
      return displayText;
    } catch (error) {
      console.error('Error formatting locale:', error);
      return locale;
    }
  };

  // Get weekday names for a given locale
  const getWeekdayNames = (locale) => {
    try {
      const weekdays = [];
      // Create a date for Monday (assuming Monday is first day)
      const date = new Date(2024, 0, 1); // January 1st, 2024 is a Monday
      
      // Get names for 7 days starting from Monday
      for (let i = 0; i < 7; i++) {
        const name = new Intl.DateTimeFormat(locale, { weekday: 'short' })
          .format(date);
        weekdays.push(name);
        date.setDate(date.getDate() + 1);
      }
      
      return {
        Mon: weekdays[0],
        Tue: weekdays[1],
        Wed: weekdays[2],
        Thu: weekdays[3],
        Fri: weekdays[4],
        Sat: weekdays[5],
        Sun: weekdays[6]
      };
    } catch (error) {
      console.error('Error getting weekday names:', error);
      return {
        Mon: 'Mon', Tue: 'Tue', Wed: 'Wed',
        Thu: 'Thu', Fri: 'Fri', Sat: 'Sat', Sun: 'Sun'
      };
    }
  };

  const handleChange = (key, value) => {
    const newProps = {
      ...func.props,
      [key]: value
    };
    onUpdate(index, newProps);
  };

  const handleNamesChange = (namesStr) => {
    // Split by commas, but don't filter out empty values
    // This allows for consecutive commas to create empty entries
    const namesArray = namesStr.split(',')
      .map(name => name.trim());
    handleChange('names', namesArray);
  };

  const handleCalendarChange = (key, value) => {
    const newCalendar = {
      ...(func.props.calendar || {}),
      [key]: value
    };
    handleChange('calendar', newCalendar);
  };

  const handleLocaleChange = (locale) => {
    // Get weekday names for the selected locale
    const weekdayNames = getWeekdayNames(locale);
    
    // Update both locale and weekday names
    const newCalendar = {
      ...(func.props.calendar || {}),
      countryLocale: locale,
      days: weekdayNames
    };
    
    handleChange('calendar', newCalendar);
  };

  // Get current locale or fallback to default
  const currentLocale = func.props.calendar?.countryLocale || 'en-US';

  return (
    <div className="event-calendar-inspector">
      <div className="calendar-section">
        <div className="input-group">
          <label>Event Date</label>
          <input
            type="date"
            value={func.props.date ? new Date(func.props.date).toISOString().split('T')[0] : ''}
            onChange={(e) => handleChange('date', new Date(e.target.value).toISOString())}
            className="date-picker"
          />
        </div>

        <div className="input-group">
          <label>Names (comma separated)</label>
          <input
            type="text"
            value={func.props.names.join(', ')}
            onChange={(e) => handleNamesChange(e.target.value)}
            placeholder="Enter names separated by commas"
            className="full-width-input"
          />
        </div>

        <div className="input-group">
          <label>Header Text</label>
          <input
            type="text"
            value={func.props.headerText || ''}
            onChange={(e) => handleChange('headerText', e.target.value)}
            placeholder="Enter header text"
          />
        </div>

        <div className="input-group">
          <label>Top Text</label>
          <input
            type="text"
            value={func.props.topText || ''}
            onChange={(e) => handleChange('topText', e.target.value)}
            placeholder="Enter text above calendar"
          />
        </div>

        <div className="input-group">
          <label>Bottom Text</label>
          <input
            type="text"
            value={func.props.bottomText || ''}
            onChange={(e) => handleChange('bottomText', e.target.value)}
            placeholder="Enter text below calendar"
          />
        </div>
      </div>

      <div className="calendar-customization">
        <h4>Calendar Customization</h4>
        
        <div className="input-group">
          <label>Locale</label>
          <select
            value={currentLocale}
            onChange={(e) => handleLocaleChange(e.target.value)}
            className="locale-select"
          >
            {availableLocales.map(locale => (
              <option key={locale} value={locale}>
                {formatLocaleDisplay(locale)}
              </option>
            ))}
          </select>
        </div>

        <div className="weekday-names">
          <h5>Weekday Names</h5>
          <div className="weekday-inputs">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <div key={day} className="weekday-input">
                <label>{day}</label>
                <input
                  type="text"
                  value={func.props.calendar?.days?.[day] || day}
                  onChange={(e) => {
                    const newDays = {
                      ...(func.props.calendar?.days || {}),
                      [day]: e.target.value
                    };
                    handleCalendarChange('days', newDays);
                  }}
                  placeholder={getWeekdayNames(currentLocale)[day]}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventCalendarInspector; 